import React, { useState } from 'react';
import siteSetting from '../../config/env';
import { fmtDuration, fmtTime12, fmtShort, initialsOf } from './bookingHelpers';
import './bookingSections.css';

/* Section components shared by the booking details page and, later, the
   receipt. Presentational: everything they show arrives as props. */

/* ---------- Icons (stroke, currentColor) ---------- */
const I = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>
);
export const IconBack = () => <I d={<path d="M19 12H5M12 19l-7-7 7-7" />} />;
export const IconShare = () => <I d={<><path d="M12 3v13" /><path d="M7 8l5-5 5 5" /><path d="M5 14v5a1 1 0 001 1h12a1 1 0 001-1v-5" /></>} />;
export const IconCalendarPlus = () => <I size={16} d={<><rect x="3" y="5" width="18" height="16" rx="2.5" /><path d="M16 3v4M8 3v4M3 10h18M12 13v5M9.5 15.5h5" /></>} />;
export const IconTick = () => <I size={16} d={<path d="M5 12.5l4.2 4.2L19 7" />} />;
export const IconPhone = () => <I size={16} d={<path d="M5 4h3.5l1.5 4-2 1.2a11 11 0 006.8 6.8L16 14l4 1.5V19a1.5 1.5 0 01-1.5 1.5A15 15 0 013.5 5.5 1.5 1.5 0 015 4z" />} />;
export const IconMail = () => <I size={16} d={<><rect x="3" y="5.5" width="18" height="13" rx="2.5" /><path d="M3.5 7l8.5 6 8.5-6" /></>} />;
export const IconDirections = () => <I size={16} d={<path d="M21 3L3 10.5l8.5 2 2 8.5L21 3z" />} />;
export const IconCopy = () => <I size={14} d={<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 012-2h9" /></>} />;
export const IconChevron = () => <I size={14} d={<path d="M9 6l6 6-6 6" />} />;
// Policy line icons: move (calendar with an arrow), cancel (circle with a
// cross), no show (a person with a slash). Monochrome, 18px.
const IconMove = () => <I d={<><rect x="3" y="5" width="18" height="16" rx="2.5" /><path d="M16 3v4M8 3v4M3 10h18M9.5 15.5h5M12.5 13.5l2 2-2 2" /></>} />;
const IconCancel = () => <I d={<><circle cx="12" cy="12" r="8.5" /><path d="M9 9l6 6M15 9l-6 6" /></>} />;
const IconNoShow = () => <I d={<><circle cx="12" cy="8.5" r="3.5" /><path d="M5 20a7 7 0 0114 0" /><path d="M4 4l16 16" /></>} />;
const POLICY_ICONS = { move: IconMove, cancel: IconCancel, noshow: IconNoShow };

/* ---------- URLs ---------- */
export const staticMapUrl = ({ lat, lng } = {}) => {
  const key = siteSetting.google_map_key;
  if (!key || typeof lat !== 'number' || typeof lng !== 'number') return '';
  const q = [
    `center=${lat},${lng}`, 'zoom=15', 'size=640x320', 'scale=2',
    'style=feature:poi%7Cvisibility:off',
    'style=feature:transit%7Celement:labels.icon%7Cvisibility:off',
    `markers=size:mid%7Ccolor:0x1a1a1a%7C${lat},${lng}`,
    `key=${key}`,
  ];
  return `https://maps.googleapis.com/maps/api/staticmap?${q.join('&')}`;
};
export const directionsUrl = ({ lat, lng } = {}, label) => {
  const dest = (typeof lat === 'number' && typeof lng === 'number') ? `${lat},${lng}` : encodeURIComponent(label || '');
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
};

/* ---------- Small pieces ---------- */

// A pill button that shows a tick and a done label for a moment after its
// action resolves: "Add to calendar" then "Added", no toast needed.
export const DonePill = ({ icon, label, doneLabel, onAct, className = '' }) => {
  const [done, setDone] = useState(false);
  const act = async (e) => {
    e.stopPropagation();
    try { await onAct(); setDone(true); setTimeout(() => setDone(false), 1400); } catch (err) { /* cancelled or refused */ }
  };
  return (
    <button type="button" className={`bs-pill${done ? ' is-done' : ''} ${className}`} onClick={act}>
      <span className="bs-pill-ic">{done ? <IconTick /> : icon}</span>
      <span>{done ? doneLabel : label}</span>
    </button>
  );
};

const linkProps = (onGo, label) => (!onGo ? {} : {
  role: 'link', tabIndex: 0, 'aria-label': label,
  onClick: onGo,
  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onGo(e); } },
});

const Avatar = ({ src, name, square }) => (
  <span className={`bs-avatar${square ? ' bs-avatar--square' : ''}`}>
    {src ? <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : initialsOf(name)}
  </span>
);

/* ---------- Sections ---------- */

// When: the date as the headline, time and duration under it, one calendar pill.
export const WhenBlock = ({ start, minutes, onAddToCalendar, calendarLabel = 'Add to calendar' }) => (
  <section className="bs-when" aria-label="When">
    <h1 className="bs-date">{start.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</h1>
    <div className="bs-time">{fmtTime12(start)}{minutes ? <span className="bs-time-meta"> · {fmtDuration(minutes)}</span> : null}</div>
    {onAddToCalendar && <DonePill icon={<IconCalendarPlus />} label={calendarLabel} doneLabel="Added" onAct={onAddToCalendar} />}
  </section>
);

// What you booked: every service with duration and price, then the money.
export const ServicesBlock = ({ services, symbol = '£', total, deposit, cardSaved, tax }) => {
  const money = (n) => `${symbol}${(Number(n) || 0).toFixed(2)}`;
  const dep = Number(deposit) || 0;
  const balance = Math.max(0, (Number(total) || 0) - dep);
  // Tax rows from the booking's snapshot (docs/tax-money-path.md §3). The
  // stored prices are gross in both modes, so the row always reads "Includes".
  const taxRows = (() => {
    if (!tax || !tax.totals || !(tax.totals.tax > 0)) return [];
    const byRate = {};
    (tax.lines || []).forEach((l) => (l.taxes || []).forEach((t) => {
      const k = `${t.name} ${t.rate}%`; byRate[k] = (byRate[k] || 0) + (Number(t.amount) || 0);
    }));
    return Object.keys(byRate).map((k) => ({ label: k, amount: byRate[k] }));
  })();
  return (
    <section className="bs-section" aria-labelledby="bs-what">
      <h2 id="bs-what" className="bs-h2">What you booked</h2>
      <ul className="bs-lines">
        {services.map((s, i) => (
          <li key={i} className="bs-line">
            <span className="bs-line-name">{s.serviceName || s.name}{s.serviceTime ? <span className="bs-line-meta"> · {fmtDuration(s.serviceTime)}</span> : null}</span>
            <span className="bs-line-amount">{money(s.servicePrice)}</span>
          </li>
        ))}
      </ul>
      <div className="bs-line bs-line--total"><span className="bs-line-name">Total</span><span className="bs-line-amount">{money(total)}</span></div>
      {taxRows.map((r) => <div className="bs-line bs-line--quiet" key={r.label}><span className="bs-line-name">Includes {r.label}</span><span className="bs-line-amount">{money(r.amount)}</span></div>)}
      {dep > 0 && <div className="bs-line bs-line--quiet"><span className="bs-line-name"><span className="bs-tick"><IconTick /></span>Deposit paid</span><span className="bs-line-amount">{money(dep)}</span></div>}
      {dep > 0 && balance > 0 && <div className="bs-line bs-line--quiet"><span className="bs-line-name">At the salon</span><span className="bs-line-amount">{money(balance)}</span></div>}
      {cardSaved && <div className="bs-line bs-line--quiet"><span className="bs-line-name"><span className="bs-tick"><IconTick /></span>Card saved for no-show protection</span><span className="bs-line-amount" /></div>}
    </section>
  );
};

// Who: the stylist, tap for their profile.
export const WhoRow = ({ name, avatar, role, onGo }) => (
  <section className="bs-section" aria-labelledby="bs-who">
    <h2 id="bs-who" className="bs-h2">Who</h2>
    <div className={`bs-row${onGo ? ' bs-row--link' : ''}`} {...linkProps(onGo, `See ${name || 'the stylist'}'s profile`)}>
      <Avatar src={avatar} name={name} />
      <div className="bs-row-text">
        <div className="bs-row-title">{name || 'Your stylist'}</div>
        <div className="bs-row-sub">{role || 'Stylist'}{onGo ? ' · See profile' : ''}</div>
      </div>
      {onGo && <span className="bs-chev"><IconChevron /></span>}
    </div>
  </section>
);

// Where: the salon row with Call and Email, the map, Get directions.
export const WhereBlock = ({ salonName, address, image, coords, phone, email, onGo }) => {
  const [mapFailed, setMapFailed] = useState(false);
  const mapSrc = staticMapUrl(coords || {});
  const dirHref = directionsUrl(coords || {}, [salonName, address].filter(Boolean).join(', '));
  const stop = (e) => e.stopPropagation();
  if (!salonName && !address) return null;
  return (
    <section className="bs-section" aria-labelledby="bs-where">
      <h2 id="bs-where" className="bs-h2">Where</h2>
      <div className={`bs-row${onGo ? ' bs-row--link' : ''}`} {...linkProps(onGo, `See ${salonName || 'the salon'}`)}>
        <Avatar src={image} name={salonName} square />
        <div className="bs-row-text">
          <div className="bs-row-title">{salonName}</div>
          {address && <div className="bs-row-sub">{address}</div>}
        </div>
        {(phone || email) && (
          <div className="bs-icons" onClick={stop} onKeyDown={stop}>
            {phone && <a className="bs-icon" href={`tel:${phone}`} aria-label={`Call ${salonName || 'the salon'}`}><IconPhone /></a>}
            {email && <a className="bs-icon" href={`mailto:${email}`} aria-label={`Email ${salonName || 'the salon'}`}><IconMail /></a>}
          </div>
        )}
      </div>
      {mapSrc && !mapFailed && (
        <a className="bs-map" href={dirHref} target="_blank" rel="noopener noreferrer" aria-label={`Map showing ${salonName || 'the salon'}. Opens directions.`}>
          <img src={mapSrc} alt="" onError={() => setMapFailed(true)} />
        </a>
      )}
      <a className="bs-btn bs-btn--ghost" href={dirHref} target="_blank" rel="noopener noreferrer"><IconDirections /><span>Get directions</span></a>
    </section>
  );
};

// Changes: what the salon allows right now, plus the booking id with copy.
export const ChangesBlock = ({ loading, lines, bookingId, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await onCopy(); setCopied(true); setTimeout(() => setCopied(false), 1400); } catch (e) { /* clipboard refused */ }
  };
  const hasLines = loading || (lines && lines.length > 0);
  if (!hasLines && !bookingId) return null;
  return (
    <section className="bs-section" aria-labelledby="bs-changes">
      {hasLines && <h2 id="bs-changes" className="bs-h2">Changes</h2>}
      {loading ? (
        <div className="bs-policy"><div className="bs-skel" style={{ height: 14, width: '70%' }} /><div className="bs-skel" style={{ height: 14, width: '50%' }} /></div>
      ) : lines && lines.length > 0 ? (
        <ul className="bs-policy">
          {lines.map((l, i) => {
            const Ic = POLICY_ICONS[l.kind] || IconMove;
            return <li key={i} className={l.ok ? 'is-ok' : 'is-off'}><span className="bs-policy-ic" aria-hidden="true"><Ic /></span><span>{l.text}</span></li>;
          })}
        </ul>
      ) : null}
      {bookingId && (
        <button type="button" className={`bs-ref${copied ? ' is-done' : ''}`} onClick={copy} aria-label="Copy booking ID">
          <span className="bs-ref-label">Booking ID</span>
          <span className="bs-ref-value">{copied ? 'Copied' : bookingId}</span>
          <span className="bs-ref-ic">{copied ? <IconTick /> : <IconCopy />}</span>
        </button>
      )}
    </section>
  );
};

// The locked bar at the foot of the screen. Positive action on the right.
export const ActionBar = ({ caption, secondary, primary, floating }) => {
  if (!secondary && !primary) return null;
  return (
    <div className={`bs-bar${floating ? ' is-floating' : ''}`}>
      <div className="bs-bar-inner">
        {caption && <p className="bs-bar-cap">{caption}</p>}
        <div className="bs-bar-cta">
          {secondary && <button type="button" className="bs-btn" onClick={secondary.onClick} disabled={secondary.disabled}>{secondary.label}</button>}
          {primary && <button type="button" className="bs-btn bs-btn--primary" onClick={primary.onClick} disabled={primary.disabled}>{primary.label}</button>}
        </div>
      </div>
    </div>
  );
};

// "Free to move until 5:00pm today" / "Free to move until Wed 23 Sept, 7:50am"
export const deadlineCaption = (by) => {
  if (!by) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(by); d.setHours(0, 0, 0, 0);
  return d.getTime() === today.getTime() ? `Free to move until ${fmtTime12(by)} today` : `Free to move until ${fmtShort(by)}`;
};
